import React, { useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAttendance } from '../contexts/AttendanceContext';
import { useRequests } from '../contexts/RequestsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import {
  User, Mail, Phone, MapPin, Calendar, Briefcase, Building, Hash, CheckCircle,
  Clock, Trophy, TrendingUp, CalendarDays, Shield, Fingerprint, Copy, Check,
  Cake, UserCircle, BadgeCheck, Timer, LogOut, Settings,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatDate } from '../utils/dateFormatter';
import { twMerge } from 'tailwind-merge';

// ---------- Tenure helper ----------
function getTenure(joinedDate?: string): string {
  if (!joinedDate) return 'N/A';
  const joined = new Date(joinedDate);
  const now = new Date();
  const years = now.getFullYear() - joined.getFullYear();
  const months = now.getMonth() - joined.getMonth();
  const totalMonths = years * 12 + months;
  if (totalMonths < 1) return 'New Employee';
  if (totalMonths < 12) return `${totalMonths} month${totalMonths > 1 ? 's' : ''}`;
  const y = Math.floor(totalMonths / 12);
  const m = totalMonths % 12;
  return m > 0 ? `${y}y ${m}m` : `${y} year${y > 1 ? 's' : ''}`;
}

// ---------- Copy button ----------
function CopyField({ value }: { value: string }): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = (): void => {
    void navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-mono bg-muted hover:bg-muted/80 transition-colors text-muted-foreground hover:text-foreground group"
      title="Click to copy"
    >
      <span className="truncate max-w-[500px]">{value}</span>
      {copied ? (
        <Check className="h-3 w-3 text-success flex-shrink-0" />
      ) : (
        <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      )}
    </button>
  );
}

// ---------- SVG ring chart ----------
function AttendanceRing({ percentage, label, color }: { percentage: number; label: string; color: string }): React.JSX.Element {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative">
        <svg width="76" height="76" viewBox="0 0 76 76">
          <circle cx="38" cy="38" r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth="6" />
          <circle
            cx="38" cy="38" r={radius}
            fill="none"
            stroke={color}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 38 38)"
            className="transition-all duration-700 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold">{percentage}%</span>
        </div>
      </div>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </div>
  );
}

export default function Profile(): React.JSX.Element | null {
  const { user, logout } = useAuth();
  const { history, monthlySummary, loadHistory } = useAttendance();
  const { leaveRequests, overtimeRequests, refreshAll } = useRequests();
  const { t } = useLanguage();

  // Load page-specific data on mount (lazy — skips if already fetched by other pages).
  useEffect(() => {
    void loadHistory();
    void refreshAll();
  }, [loadHistory, refreshAll]);

  if (!user) return null;

  const getInitials = (name: string): string =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  // Compute attendance stats
  const attendanceStats = useMemo(() => {
    const total = history.length;
    const onTime = history.filter((r) => r.status === 'on_time').length;
    const late = history.filter((r) => r.status === 'late').length;
    const onTimePercent = total > 0 ? Math.round((onTime / total) * 100) : 0;

    const approvedLeaves = leaveRequests.filter((r) => r.status === 'approved').length;
    const totalOtHours = overtimeRequests
      .filter((r) => r.status === 'approved')
      .reduce((sum, r) => sum + r.totalHours, 0);

    return { total, onTime, late, onTimePercent, approvedLeaves, totalOtHours };
  }, [history, leaveRequests, overtimeRequests]);

  // Info fields grouped into sections
  const personalInfo = [
    { icon: Mail, label: t.email, value: user.email },
    { icon: Phone, label: t.phone, value: user.phone ?? t.notProvided },
    { icon: Cake, label: t.birthDate, value: user.birthDate ? formatDate(user.birthDate) : t.notProvided },
    { icon: UserCircle, label: t.gender, value: user.gender },
    { icon: MapPin, label: t.address, value: user.address ?? t.notProvided },
  ];

  const employmentInfo = [
    { icon: Hash, label: t.employeeCode, value: user.code },
    { icon: Building, label: t.department, value: user.departmentId },
    { icon: Briefcase, label: t.organization, value: user.organizationId },
    { icon: Calendar, label: t.joined, value: user.joinedDate ? formatDate(user.joinedDate) : t.notProvided },
  ];

  return (
    <div className="space-y-4 animate-fade-in" data-testid="profile-page">
      {/* ===== Hero Header Card ===== */}
      <Card className="shadow-lg overflow-hidden border-0">
        <div
          className="relative overflow-hidden px-5 pt-5 pb-16"
          style={{ background: 'var(--gradient-primary)' }}
        >
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute -top-8 -right-8 h-40 w-40 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -bottom-10 -left-6 h-32 w-32 rounded-full bg-black/10" />
          <div className="pointer-events-none absolute top-4 right-24 h-16 w-16 rounded-full bg-white/8" />

          {/* Label row */}
          <div className="flex items-center gap-2 mb-5">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 shadow-inner">
              <User className="h-4 w-4 text-white" />
            </div>
            <span className="text-[11px] font-bold text-white/80 uppercase tracking-[0.15em]">{t.navProfile}</span>
          </div>

          {/* Avatar + Name row */}
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <Avatar className="h-24 w-24 border-4 border-white/20 shadow-lg">
                <AvatarFallback className="bg-white text-primary text-3xl font-bold">
                  {getInitials(user.fullName)}
                </AvatarFallback>
              </Avatar>
              {user.status === 'active' && (
                <div className="absolute bottom-0.5 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-success border-2 border-white shadow-sm">
                  <CheckCircle className="h-3.5 w-3.5 text-white" />
                </div>
              )}
            </div>
            <div className="text-white flex-1 min-w-0">
              <h1 className="text-[26px] font-black leading-tight tracking-tight mb-2 truncate">{user.fullName}</h1>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="bg-white/15 text-white border-white/20 backdrop-blur-sm text-[11px] px-2.5 py-1">
                  <Hash className="h-3 w-3 mr-1" />{user.code}
                </Badge>
                <Badge variant="secondary" className="bg-white/15 text-white border-white/20 backdrop-blur-sm text-[11px] px-2.5 py-1 capitalize">
                  <UserCircle className="h-3 w-3 mr-1" />{user.gender}
                </Badge>
                <Badge variant="secondary" className="bg-white/15 text-white border-white/20 backdrop-blur-sm text-[11px] px-2.5 py-1">
                  <Timer className="h-3 w-3 mr-1" />
                  {getTenure(user.joinedDate) === 'New Employee' ? t.newEmployee : getTenure(user.joinedDate)}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Row - overlapping hero */}
        <CardContent className="-mt-10 relative z-10 px-4 sm:px-6 pb-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-card rounded-xl border shadow-sm p-5 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 mx-auto mb-2.5">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <p className="text-2xl font-black">{attendanceStats.onTimePercent}%</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.punctuality}</p>
            </div>
            <div className="bg-card rounded-xl border shadow-sm p-5 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg mx-auto mb-2.5" style={{ background: 'hsl(var(--success) / 0.15)' }}>
                <CalendarDays className="h-5 w-5" style={{ color: 'hsl(var(--success))' }} />
              </div>
              <p className="text-2xl font-black" style={{ color: 'hsl(var(--success))' }}>{attendanceStats.total}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.daysAttended}</p>
            </div>
            <div className="bg-card rounded-xl border shadow-sm p-5 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg mx-auto mb-2.5" style={{ background: 'hsl(var(--accent) / 0.15)' }}>
                <CalendarDays className="h-5 w-5" style={{ color: 'hsl(var(--accent))' }} />
              </div>
              <p className="text-2xl font-black" style={{ color: 'hsl(var(--accent))' }}>{attendanceStats.approvedLeaves}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.leavesUsed}</p>
            </div>
            <div className="bg-card rounded-xl border shadow-sm p-5 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg mx-auto mb-2.5" style={{ background: 'hsl(var(--warning) / 0.15)' }}>
                <Clock className="h-5 w-5" style={{ color: 'hsl(var(--warning))' }} />
              </div>
              <p className="text-2xl font-black" style={{ color: 'hsl(var(--warning))' }}>{attendanceStats.totalOtHours}h</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.overtime}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== Main Content Grid ===== */}
      <div className="grid gap-4">
        {/* Left column: Info cards */}
        <div className="space-y-4">
          {/* Personal Information */}
          <Card className="shadow-md overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'var(--gradient-primary)' }} />
            <CardHeader className="px-4 pb-3 pt-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-[16px] font-black">{t.personalInfo}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.personalInfoDesc}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="divide-y divide-border/50">
                {personalInfo.map((field) => (
                  <div key={field.label} className="flex items-center gap-3 py-3.5 first:pt-1 last:pb-0 group">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted group-hover:bg-primary/10 transition-colors flex-shrink-0">
                      <field.icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0 gap-2">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold w-28 flex-shrink-0">{field.label}</p>
                      <p className={twMerge('text-sm font-semibold text-foreground break-words flex-1', field.label === 'Gender' ? 'capitalize' : '')}>{field.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Employment Information */}
          <Card className="shadow-md overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, hsl(var(--accent)), hsl(var(--accent) / 0.6))' }} />
            <CardHeader className="px-4 pb-3 pt-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: 'hsl(var(--accent) / 0.15)' }}>
                  <Briefcase className="h-5 w-5" style={{ color: 'hsl(var(--accent))' }} />
                </div>
                <div>
                  <CardTitle className="text-[16px] font-black">{t.employmentDetails}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.employmentDetailsDesc}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="divide-y divide-border/50">
                {employmentInfo.map((field) => (
                  <div key={field.label} className="flex items-center gap-3 py-3.5 first:pt-1 last:pb-0 group">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted group-hover:bg-primary/10 transition-colors flex-shrink-0">
                      <field.icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0 gap-2">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold w-28 flex-shrink-0">{field.label}</p>
                      <p className="text-sm font-semibold text-foreground break-words flex-1">{field.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column: Performance & System */}
        <div className="space-y-4">
          {/* Attendance Performance */}
          <Card className="shadow-md overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, hsl(var(--success)), #34d399)' }} />
            <CardHeader className="pb-3 pt-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: 'color-mix(in srgb, var(--success) 15%, transparent)' }}>
                  <Trophy className="h-5 w-5" style={{ color: 'var(--success)' }} />
                </div>
                <CardTitle className="text-[16px] font-black">{t.performanceTitle}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-5">
              {/* Ring charts */}
              <div className="flex justify-around mb-5 py-2">
                <AttendanceRing
                  percentage={attendanceStats.onTimePercent}
                  label={t.onTime}
                  color="hsl(var(--success))"
                />
                <AttendanceRing
                  percentage={attendanceStats.total > 0 ? Math.round((attendanceStats.late / attendanceStats.total) * 100) : 0}
                  label={t.late}
                  color="hsl(var(--warning))"
                />
              </div>

              <Separator className="mb-4" />

              {/* Monthly summary */}
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-muted-foreground font-medium">{t.onTime}</span>
                    <span className="font-black" style={{ color: 'var(--success)' }}>{monthlySummary?.onTimeDays ?? 0} {t.days}</span>
                  </div>
                  <Progress value={monthlySummary && monthlySummary.totalDays > 0 ? (monthlySummary.onTimeDays / monthlySummary.totalDays) * 100 : 0} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-muted-foreground font-medium">{t.late}</span>
                    <span className="font-black" style={{ color: 'var(--warning)' }}>{monthlySummary?.lateDays ?? 0} {t.days}</span>
                  </div>
                  <Progress value={monthlySummary && monthlySummary.totalDays > 0 ? (monthlySummary.lateDays / monthlySummary.totalDays) * 100 : 0} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-muted-foreground font-medium">{t.avgHoursDay}</span>
                    <span className="font-black text-foreground">{monthlySummary?.avgHoursPerDay ?? '0'}h</span>
                  </div>
                  <Progress value={monthlySummary ? (Number(monthlySummary.avgHoursPerDay) / 9) * 100 : 0} className="h-2" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account IDs */}
          <Card className="shadow-sm overflow-hidden">
            <div className="h-1 w-full bg-muted" />
            <CardHeader className="pb-3 pt-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <Fingerprint className="h-5 w-5 text-muted-foreground" />
                </div>
                <CardTitle className="text-[16px] font-black">{t.accountIds}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="divide-y divide-border/50">
                {[
                  { label: t.userUuid, value: user.uuid },
                  { label: t.orgCode, value: user.organizationId },
                  { label: t.department, value: user.departmentId },
                ].map(({ label, value }) => (
                  <div key={label} className="py-3 first:pt-1 last:pb-0">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold mb-1.5">{label}</p>
                    <CopyField value={value} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ===== Settings ===== */}
      <Link
        to="/settings"
        className="w-full flex items-center justify-center gap-2.5 rounded-2xl py-4 text-sm font-black text-foreground border border-border bg-muted/30 hover:bg-muted/60 transition-all active:scale-[0.98]"
      >
        <Settings className="h-4 w-4" />
        {t.navSettings}
      </Link>

      {/* ===== Logout ===== */}
      <button
        className="w-full flex items-center justify-center gap-2.5 rounded-2xl py-4 text-sm font-black text-destructive border border-destructive/30 bg-destructive/5 hover:bg-destructive/10 transition-all active:scale-[0.98]"
        onClick={() => void logout()}
      >
        <LogOut className="h-4 w-4" />
        {t.logout}
      </button>

      {/* ===== Footer Credit ===== */}
      <div className="pb-2 text-center">
        <p className="text-[11px] text-muted-foreground">
          {t.developedBy}{' '}
          <a
            href="https://eltera.id"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-primary hover:underline"
          >
            Eltera
          </a>
        </p>
      </div>
    </div>
  );
}
